import { toError } from '@main/shared/utils';
import { useNhostClient } from '@nhost/react';
import { useEffect, useState } from 'react';

export type UsePresignedUrlResult = {
  url: string | undefined;
  isLoading: boolean;
  error: Error | null;
};

type NhostStorage = ReturnType<typeof useNhostClient>['storage'];

// getPresignedUrl has no AbortSignal support, so a mount's own cleanup
// can't cancel its in-flight request, only discard the result. Without
// this, React 18 StrictMode's dev-only double-invoke (mount, cleanup,
// mount again) sends two real network requests per file, the first can
// even 404 if it lands before the storage backend's read path has caught
// up with a just-created file. Cache the in-flight promise per fileId so
// a second call (StrictMode's remount, or any other concurrent consumer)
// awaits the same request instead of firing a new one.
const inFlightByFileId = new Map<string, Promise<UsePresignedUrlResult>>();

function fetchPresignedUrl(storage: NhostStorage, fileId: string): Promise<UsePresignedUrlResult> {
  const existing = inFlightByFileId.get(fileId);
  if (existing) return existing;

  const request = (async (): Promise<UsePresignedUrlResult> => {
    try {
      const { presignedUrl, error: nhostError } = await storage.getPresignedUrl({ fileId });

      if (nhostError) {
        throw toError(nhostError);
      }

      return { url: presignedUrl?.url, isLoading: false, error: null };
    } catch (err) {
      return {
        url: undefined,
        isLoading: false,
        error: toError(err),
      };
    } finally {
      inFlightByFileId.delete(fileId);
    }
  })();

  inFlightByFileId.set(fileId, request);
  return request;
}

export function usePresignedUrl(fileId: string | undefined): UsePresignedUrlResult {
  const { storage } = useNhostClient();
  const [state, setState] = useState<UsePresignedUrlResult>({
    url: undefined,
    isLoading: false,
    error: null,
  });

  useEffect(() => {
    if (!fileId) {
      setState({ url: undefined, isLoading: false, error: null });
      return;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, isLoading: true }));

    void fetchPresignedUrl(storage, fileId).then((result) => {
      if (!cancelled) {
        setState(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fileId, storage]);

  return state;
}
